// src/modules/units/tyres/views/create/newtyreform.js
//
// Tyre record form orchestrator — create and edit.
//
//  - create: builds the payload, sources the customer from the app's global
//    selection, requires a yard for the new tyre (defaulting to the yard in
//    scope), checks whether the entered ID already exists (upsert guard) and
//    confirms before overwriting, then saves in a SINGLE POST. On success it
//    lands on the new tyre's detail (onCreated).
//  - edit: prefilled by the view; the ID is locked. The yard IS editable here so
//    legacy tyres with no yard can be assigned one; the change saves through the
//    same record upsert (it is not a logged "move" — a dedicated transfer action
//    can replace this when the backend endpoint lands). Returns to the detail
//    (onClose), which refetches.
//
// Standalone create records a new tyre In Stock. When this same form is opened
// from a specific wheel, ctx.targetSensorId is supplied and the saved tyre is
// immediately associated with that wheel's sensor. This replaces the retired
// legacy wheel-context modal while keeping one create form and one data model.
//
// Neither mode announces the change itself: tyres.api.js emits CHANGED on every
// successful write, so open views refetch whether or not this form gets that far.

import {
  fetchTyre,
  saveTyre,
  associateTyreWithSensor,
  clearTyreSensorAssociation,
  archiveTyre,
  fetchArchivedTyres,
  restoreTyre
} from '../tyres.api.js';
import { isArchiveEligibleTyre } from '../../shared/tyre-status.js';
import { canArchiveTyres } from '../../shared/tyre-access.js';
import { fetchSuppliers } from '../suppliers.api.js';
import { fetchTyreCatalogue } from '../tyre-catalogue.api.js';
import { fetchYards } from '../yards.api.js';
import { getSelectedYard } from '../../shared/tyre-scope.js';
import { hasDistanceTracking } from '../../shared/tyre-metrics.js';
import {
  normaliseCatalogueValue, normaliseTyreSize, uniqueCatalogueValues,
  catalogueModelRows, singleCatalogueTread, narrowCatalogueRows, catalogueDefaultsFor
} from '../../shared/tyre-catalogue-match.js';
import { tyreFormHTML, fieldsFor, archivedIdConflictHTML, ARCHIVED_ID_HINT } from './newtyreform.view.js';

const isBlank = (v) => v === null || v === undefined || v === '';

function existingTyreIdFromAssociation(res) {
  const d = res && res.data;
  return (
    d?.existingTyre?.externalTyreId ||
    d?.conflictingTyre?.externalTyreId ||
    d?.existingExternalTyreId ||
    null
  );
}

// Opening distance is a one-off baseline for an old tyre entering the system.
// Once automatic distance tracking has started (hasDistanceTracking, shared with
// the importer in tyre-metrics.js) the baseline becomes read-only.

function supplierIdFromTyre(tyre = {}) {
  return tyre.supplierId ?? tyre.supplier?.supplierId ?? '';
}

function supplierNameFromTyre(tyre = {}) {
  if (typeof tyre.supplier === 'string') return tyre.supplier;
  return tyre.supplierName ?? tyre.supplier?.supplierName ?? '';
}

// tyreRFID is not in the API contract yet — the backend is adding it. We post it
// anyway so a scanned tag isn't silently thrown away, and it lights up the moment
// the backend lands. If your validator rejects unknown properties and creates
// start failing with a 400, set this to false: the input keeps working, the value
// just isn't sent until then.
const SEND_PENDING_FIELDS = true;

// The app tracks the selected customer on the global object; read it defensively.
function currentCustomerId() {
  if (typeof global !== 'undefined' && global && global.customerId) return global.customerId;
  if (typeof globalThis !== 'undefined' && globalThis.customerId) return globalThis.customerId;
  return undefined;
}

function escapeHtml(v) {
  return String(v ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

export function renderTyreForm(viewEl, ctx = {}) {
  let disposed = false;
  let busy = false;
  const isEdit = ctx.mode === 'edit';
  const canArchive = isEdit && canArchiveTyres() && isArchiveEligibleTyre(ctx.tyre);
  const seed = ctx.tyre || {};
  const targetSensorId = isEdit ? null : (ctx.targetSensorId ?? null);
  const fitTargetLabel = ctx.fitTargetLabel || (targetSensorId ? `sensor ${targetSensorId}` : '');

  const fields = fieldsFor(isEdit ? 'edit' : 'create');
  const numberKeys = new Set(fields.filter((f) => f.control === 'number').map((f) => f.key));
  const treadDepthKeys = new Set([
    'initialTyreTreadDepth',
    'minimumTreadForRetread',
    'tyreTreadDepth'
  ]);
  // Fields that belong in the POST body: everything except, optionally, pending
  // ones. (There are no association fields on this form any more.)
  // const bodyFields = fields.filter((f) => !f.association && (SEND_PENDING_FIELDS || !f.pending));
  const bodyFields = fields.filter(
    (f) =>
      !f.association &&
      !f.localOnly &&
      !f.pendingBackend &&
      (SEND_PENDING_FIELDS || !f.pending)
  );
  const tpl = document.createElement('template');
  tpl.innerHTML = tyreFormHTML(isEdit ? 'edit' : 'create', seed, { fitTargetLabel, canArchive });
  const root = tpl.content.firstElementChild;
  const msgEl = root.querySelector('[data-msg]');
  const submitBtn = root.querySelector('[data-submit]');
  const cancelBtn = root.querySelector('[data-cancel]');
  const idInput = root.querySelector('[data-field="externalTyreId"]');
  const yardSel = root.querySelector('[data-dynamic="yards"]');
  const supplierSel =
    root.querySelector('[data-dynamic="suppliers"]');
  const manufacturerInput = root.querySelector('[data-catalogue-typeahead="manufacturer"]');
  const modelInput = root.querySelector('[data-catalogue-typeahead="model"]');
  const treadInput = root.querySelector('[data-catalogue-typeahead="tread"]');
  const sizeInput = root.querySelector('[data-field="tyreSize"]');
  const applicationInput = root.querySelector('[data-field="tyreApplication"]');
  const typeInput = root.querySelector('[data-field="tyreType"]');
  const gradeInput = root.querySelector('[data-field="tyreGrade"]');
  const manufacturerList = root.querySelector('datalist[data-catalogue-list="manufacturer"]');
  const modelList = root.querySelector('datalist[data-catalogue-list="model"]');
  const treadList = root.querySelector('datalist[data-catalogue-list="tread"]');
  let catalogueChoices = [];
  let lastAutoNominalTread = null;
  let lastAutoCurrentTread = null;
  let lastAutoTreadPattern = null;
  const autoCatalogueValues = new Map();
  if (!isEdit && isBlank(seed.tyreGrade)) autoCatalogueValues.set('tyreGrade', 'New');
  const initInput = root.querySelector('[data-field="initialTyreTreadDepth"]');
  const curInput = root.querySelector('[data-field="tyreTreadDepth"]');
  const minRetreadInput =
    root.querySelector('[data-field="minimumTreadForRetread"]');
  const distanceInput = root.querySelector('[data-field="tyreKm"]');
  let distanceEditable = !hasDistanceTracking(seed);

  const expectedMileageInput =
    root.querySelector('[data-field="manufacturerExpectedMileage"]');

  const expectedKmPerMmEl =
    root.querySelector('[data-calculated="expectedKmPerMm"]');
  const conflictEl = root.querySelector('[data-conflict]');
  const conflictMsg = root.querySelector('[data-conflict-msg]');
  const conflictConfirm = root.querySelector('[data-conflict-confirm]');
  const conflictCancel = root.querySelector('[data-conflict-cancel]');
  const submitLabel = submitBtn.textContent;
  const archiveOpenBtn = root.querySelector('[data-archive-open]');
  const archivePanel = root.querySelector('[data-archive-panel]');
  const archiveCancelBtn = root.querySelector('[data-archive-cancel]');
  const archiveConfirmBtn = root.querySelector('[data-archive-confirm]');
  const archiveMsg = root.querySelector('[data-archive-msg]');
  const archiveId = String(seed.externalTyreId ?? '');
  const archiveConfirmLabel = archiveConfirmBtn?.textContent || 'Archive tyre';
  let archiveStillAllowed = canArchive;
  let conflictCancelAction = null;

  viewEl.replaceChildren(root);
  if (!isEdit && idInput) idInput.focus();

  function applyOpeningDistanceRule(tyre = {}) {
    distanceEditable = !hasDistanceTracking(tyre);
    if (!distanceInput) return;

    distanceInput.disabled = !distanceEditable;
    distanceInput.classList.toggle('tm-input-locked', !distanceEditable);
    distanceInput.title = distanceEditable
      ? 'Opening distance may be entered until automatic distance tracking starts.'
      : 'Automatic distance tracking has started. Distance is maintained by PressureTrack.';
  }

  applyOpeningDistanceRule(seed);

  // Fill the yard list. Create defaults to the yard in scope; edit defaults to
  // the tyre's current yard (blank for a legacy yard-less tyre, which the
  // required-yard check then forces the user to set).
  if (yardSel) populateYards();
  if (supplierSel) populateSuppliers();
  if (manufacturerInput || modelInput || treadInput) populateCatalogueChoices();

  // create only: a brand-new tyre's current tread starts at its initial tread.
  if (!isEdit && initInput && curInput) {
    initInput.addEventListener('input', () => { if (isBlank(curInput.value)) curInput.value = initInput.value; });
  }

  // Calculate the manufacturer's expected km per 1 mm of usable tread.
  function updateExpectedKmPerMm() {
    if (!expectedKmPerMmEl) return;

    const initial =
      initInput && initInput.value !== ''
        ? Number(initInput.value)
        : null;

    const minimum =
      minRetreadInput && minRetreadInput.value !== ''
        ? Number(minRetreadInput.value)
        : null;

    const mileage =
      expectedMileageInput && expectedMileageInput.value !== ''
        ? Number(expectedMileageInput.value)
        : null;

    if (
      initial === null ||
      minimum === null ||
      mileage === null ||
      !Number.isFinite(initial) ||
      !Number.isFinite(minimum) ||
      !Number.isFinite(mileage) ||
      initial <= minimum ||
      mileage <= 0
    ) {
      expectedKmPerMmEl.textContent = '—';
      return;
    }

    const usableTread = initial - minimum;
    const kmPerMm = mileage / usableTread;

    expectedKmPerMmEl.textContent =
      `${Math.round(kmPerMm).toLocaleString('en-ZA')} km per 1 mm`;
  }

  // Recalculate whenever one of the three inputs changes.
  [initInput, minRetreadInput, expectedMileageInput]
    .filter(Boolean)
    .forEach((input) => {
      input.addEventListener('input', updateExpectedKmPerMm);
    });

  // Also calculate immediately when editing an existing tyre.
  updateExpectedKmPerMm();

  // A barcode scanner types the code and then sends Enter. Chain the scan boxes so
  // all codes go in without ever touching the mouse — and swallow the Enter so a
  // stray one can never fire a save.
  const scanInputs = Array.from(root.querySelectorAll('[data-scan]'));
  scanInputs.forEach((input, i) => {
    input.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      const next = scanInputs[i + 1];
      if (next) { next.focus(); next.select(); }
    });
  });

  function setCatalogueDatalist(listEl, values, labelFor = null) {
    if (!listEl) return;
    listEl.innerHTML = values.map((value) => {
      const label = typeof labelFor === 'function' ? labelFor(value) : '';
      return `<option value="${escapeHtml(value)}"${label ? ` label="${escapeHtml(label)}"` : ''}></option>`;
    }).join('');
  }

  function filteredCatalogueRows() {
    let rows = catalogueChoices;
    const manufacturer = normaliseCatalogueValue(manufacturerInput?.value);
    const model = normaliseCatalogueValue(modelInput?.value);

    if (manufacturer) {
      const exactManufacturer = rows.some((row) =>
        normaliseCatalogueValue(row.manufacturerName) === manufacturer
      );
      if (exactManufacturer) {
        rows = rows.filter((row) => normaliseCatalogueValue(row.manufacturerName) === manufacturer);
      }
    }

    const rowsAfterManufacturer = rows;
    if (model) {
      const exactModel = rows.some((row) => normaliseCatalogueValue(row.modelName) === model);
      if (exactModel) {
        rows = rows.filter((row) => normaliseCatalogueValue(row.modelName) === model);
      }
    }

    return { rowsAfterManufacturer, rows };
  }

  function refreshCatalogueTypeaheads() {
    setCatalogueDatalist(
      manufacturerList,
      uniqueCatalogueValues(catalogueChoices, 'manufacturerName')
    );

    const { rowsAfterManufacturer, rows } = filteredCatalogueRows();
    setCatalogueDatalist(
      modelList,
      uniqueCatalogueValues(rowsAfterManufacturer, 'modelName')
    );

    const treadValues = uniqueCatalogueValues(rows, 'treadPatternCode');
    setCatalogueDatalist(treadList, treadValues, (value) => {
      const match = rows.find((row) =>
        normaliseCatalogueValue(row.treadPatternCode) === normaliseCatalogueValue(value)
      );
      return match?.treadPatternDescription || '';
    });
  }

  function setSelectValue(select, value) {
    if (!select) return;
    const text = String(value ?? '').trim();
    if (!text) {
      select.value = '';
      return;
    }
    const exists = Array.from(select.options).some((option) => option.value === text);
    if (!exists) select.add(new Option(text, text));
    select.value = text;
  }

  function fieldNormaliser(key) {
    return key === 'tyreSize' ? normaliseTyreSize : normaliseCatalogueValue;
  }

  // Catalogue values only fill a field while it is blank or while the current
  // value is one we previously filled automatically. That means an existing
  // tyre's recorded/manual specification always wins over catalogue defaults.
  function setAutoCatalogueField(key, input, value) {
    if (!input) return;
    const normalise = fieldNormaliser(key);
    const previousAuto = autoCatalogueValues.get(key);
    const currentIsAuto = previousAuto !== undefined &&
      normalise(input.value) === normalise(previousAuto);

    if (isBlank(value)) {
      if (currentIsAuto) {
        input.value = '';
        autoCatalogueValues.delete(key);
      }
      return;
    }

    if (!isBlank(input.value) && !currentIsAuto) return;

    if (input.tagName === 'SELECT') setSelectValue(input, value);
    else input.value = String(value);
    autoCatalogueValues.set(key, value);
  }

  function setAutoNominalTread(value) {
    if (!initInput) return;
    const nominal = Number(value);
    const currentInitial = initInput.value === '' ? null : Number(initInput.value);
    const currentIsAuto = lastAutoNominalTread !== null &&
      Number(currentInitial) === Number(lastAutoNominalTread);

    if (!Number.isFinite(nominal) || nominal <= 0) {
      if (currentIsAuto) {
        initInput.value = '';
        lastAutoNominalTread = null;
        if (!isEdit && curInput && lastAutoCurrentTread !== null &&
            Number(curInput.value) === Number(lastAutoCurrentTread)) {
          curInput.value = '';
          lastAutoCurrentTread = null;
        }
        updateExpectedKmPerMm();
      }
      return;
    }

    if (currentInitial !== null && !currentIsAuto) return;

    initInput.value = String(nominal);
    lastAutoNominalTread = nominal;

    // On create, current tread starts at the brand-new tread while it is blank
    // or still contains the previous catalogue-supplied nominal value.
    if (!isEdit && curInput) {
      const currentTreadIsAuto = lastAutoCurrentTread !== null &&
        Number(curInput.value) === Number(lastAutoCurrentTread);
      if (isBlank(curInput.value) || currentTreadIsAuto) {
        curInput.value = String(nominal);
        lastAutoCurrentTread = nominal;
      }
    }
    updateExpectedKmPerMm();
  }

  // The matching rule itself is shared with the import template
  // (tyre-catalogue-match.js); this function adds only the form's side of it —
  // writing an inferred tread into the input and retracting one that no longer fits.
  function matchingCatalogueRows({ inferSingleTread = false } = {}) {
    const modelMatches = catalogueModelRows(catalogueChoices, manufacturerInput?.value, modelInput?.value);
    if (!modelMatches.length) return [];

    let tread = normaliseCatalogueValue(treadInput?.value);
    if (treadInput && lastAutoTreadPattern !== null &&
        normaliseCatalogueValue(treadInput.value) === normaliseCatalogueValue(lastAutoTreadPattern) &&
        !modelMatches.some((row) => normaliseCatalogueValue(row.treadPatternCode) === tread)) {
      treadInput.value = '';
      lastAutoTreadPattern = null;
      tread = '';
    }

    if (!tread && inferSingleTread && treadInput) {
      const only = singleCatalogueTread(modelMatches);
      if (only) {
        const previousAuto = lastAutoTreadPattern;
        const currentIsAuto = previousAuto !== null &&
          normaliseCatalogueValue(treadInput.value) === normaliseCatalogueValue(previousAuto);
        if (isBlank(treadInput.value) || currentIsAuto) {
          treadInput.value = only;
          lastAutoTreadPattern = only;
          tread = normaliseCatalogueValue(only);
        }
      }
    }

    // Size is part of the catalogue specification: a size already on the tyre
    // (especially in Edit Tyre) chooses the most specific rows.
    return narrowCatalogueRows(modelMatches, { tread, size: sizeInput?.value });
  }

  function applyCatalogueMatch({ inferSingleTread = false } = {}) {
    const matches = matchingCatalogueRows({ inferSingleTread });
    if (!matches.length) return;

    // When several catalogue rows share manufacturer/model/tread (for example
    // different rim sizes), a value is suggested only if every row agrees
    // (catalogueDefaultsFor — the rule the import template uses too). Once the
    // user chooses a size the match normally collapses to one row.
    const d = catalogueDefaultsFor(matches);
    setAutoCatalogueField('tyreSize', sizeInput, d.tyreSize);
    setAutoCatalogueField('tyreApplication', applicationInput, d.tyreApplication);
    setAutoCatalogueField('tyreType', typeInput, d.tyreType);
    setAutoCatalogueField('tyreGrade', gradeInput, d.tyreGrade);
    setAutoCatalogueField('manufacturerExpectedMileage', expectedMileageInput, d.manufacturerExpectedMileage);
    setAutoCatalogueField('minimumTreadForRetread', minRetreadInput, d.minimumTreadForRetread);
    setAutoNominalTread(d.initialTyreTreadDepth);
    updateExpectedKmPerMm();
  }

  async function populateCatalogueChoices() {
    // Use the full catalogue here rather than /choices so the Edit Tyre form has
    // access to the new specification fields as well. Inactive entries are still
    // excluded from typeahead/default behaviour.
    const res = await fetchTyreCatalogue({ sort: 'manufacturerName,modelName' });
    if (disposed) return;

    catalogueChoices = res?.success && Array.isArray(res.data?.results)
      ? res.data.results.filter((row) => row?.isActive !== false)
      : [];
    refreshCatalogueTypeaheads();

    // In Edit Tyre this fills only missing specification values; it never
    // overwrites data already recorded on the physical tyre.
    applyCatalogueMatch({ inferSingleTread: true });
  }

  [manufacturerInput, modelInput, treadInput].filter(Boolean).forEach((input) => {
    input.addEventListener('input', refreshCatalogueTypeaheads);
    input.addEventListener('change', () => {
      refreshCatalogueTypeaheads();
      applyCatalogueMatch({ inferSingleTread: true });
    });
    input.addEventListener('blur', () => applyCatalogueMatch({ inferSingleTread: true }));
  });

  if (sizeInput) {
    sizeInput.addEventListener('change', () => applyCatalogueMatch({ inferSingleTread: true }));
  }

  // Load the yard options and default-select the preferred yard: the tyre's own
  // on edit, the yard in scope on create. A legacy tyre has neither, leaving the
  // placeholder so the required-yard check forces a choice.
  async function populateYards() {
    const res = await fetchYards();
    if (disposed || !yardSel) return;

    const yards = res && res.success ? (res.data?.results || []) : [];

    // No yards have been created yet
    if (yards.length === 0) {
      yardSel.innerHTML =
        '<option value="">No yards yet — create a yard first</option>';

      yardSel.disabled = true;
      return;
    }

    // We do have yards
    yardSel.disabled = false;

    const preferredId = isEdit
      ? (seed.yardId || '')
      : (getSelectedYard()?.Id || '');

    const opts = ['<option value="">Select a yard</option>'].concat(
      yards.map((y) => {
        const label = y.customerName
          ? `${y.name} · ${y.customerName}`
          : y.name;

        const sel =
          String(y.Id) === String(preferredId)
            ? ' selected'
            : '';

        return `<option value="${escapeHtml(y.Id)}"${sel}>${escapeHtml(label)}</option>`;
      })
    );

    yardSel.innerHTML = opts.join('');

    if (preferredId) {
      yardSel.value = preferredId;
    }
  }

  async function populateSuppliers() {
    const res = await fetchSuppliers({ sort: 'supplierName' });

    if (disposed || !supplierSel) return;

    const suppliers =
      res && res.success
        ? (res.data?.results || [])
        : [];

    // No suppliers exist yet
    if (suppliers.length === 0) {
      supplierSel.innerHTML =
        '<option value="">No suppliers yet — create a supplier first</option>';

      supplierSel.disabled = true;
      return;
    }

    supplierSel.disabled = false;

    let currentSupplierId = isEdit ? supplierIdFromTyre(seed) : '';
    if (!currentSupplierId && isEdit) {
      const legacyName = String(supplierNameFromTyre(seed) || '').trim().toLowerCase();
      const match = legacyName
        ? suppliers.find((supplier) =>
          String(supplier.supplierName || '').trim().toLowerCase() === legacyName
        )
        : null;
      currentSupplierId = match?.supplierId || '';
    }

    const opts = [
      '<option value="">Select a supplier</option>'
    ].concat(
      suppliers.map((supplier) => {
        const selected =
          String(supplier.supplierId) === String(currentSupplierId)
            ? ' selected'
            : '';

        return `
        <option
          value="${escapeHtml(supplier.supplierId)}"${selected}
        >
          ${escapeHtml(supplier.supplierName)}
        </option>`;
      })
    );

    supplierSel.innerHTML = opts.join('');

    if (currentSupplierId) {
      supplierSel.value = currentSupplierId;
    }
  }

  function readValues() {
    const v = {};
    root.querySelectorAll('[data-field]').forEach((e) => { v[e.getAttribute('data-field')] = e.value; });
    return v;
  }
  function setMsg(t, k) {
    msgEl.textContent = t || '';
    msgEl.hidden = !t;
    msgEl.className = 'tm-form-msg' + (k ? ` tm-form-msg-${k}` : '');
  }
  function setBusy(on, label) {
    busy = on;
    submitBtn.disabled = on;
    cancelBtn.disabled = on;
    submitBtn.textContent = on ? (label || 'Saving…') : submitLabel;
    if (archiveOpenBtn) archiveOpenBtn.disabled = on || !archiveStillAllowed;
    if (archiveCancelBtn) archiveCancelBtn.disabled = on;
    if (archiveConfirmBtn) archiveConfirmBtn.disabled = on || !archiveStillAllowed;
  }

  function setArchiveMsg(text) {
    if (!archiveMsg) return;
    archiveMsg.textContent = text || '';
    archiveMsg.hidden = !text;
    archiveMsg.className = 'tm-form-msg tm-form-msg-error';
  }

  function closeArchivePanel() {
    archivePanel.hidden = true;
    archiveOpenBtn.hidden = false;
    setArchiveMsg('');
    archiveOpenBtn.focus();
  }

  // The server is the security boundary; this check only controls the UI.
  if (canArchive && archiveOpenBtn && archivePanel && archiveConfirmBtn) {
    archiveOpenBtn.addEventListener('click', () => {
      if (busy || !archiveStillAllowed) return;
      archiveOpenBtn.hidden = true;
      archivePanel.hidden = false;
      setArchiveMsg('');
      // Focus the safe choice, so a stray Enter keeps the tyre.
      archiveCancelBtn?.focus();
    });
    archiveCancelBtn?.addEventListener('click', () => {
      if (busy) return;
      closeArchivePanel();
    });
    archivePanel.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !busy) { e.stopPropagation(); closeArchivePanel(); }
    });
    archiveConfirmBtn.addEventListener('click', async () => {
      if (busy || !archiveStillAllowed) return;
      // Permissions may have changed while the form was open; the backend also verifies.
      if (!canArchiveTyres()) {
        setArchiveMsg('Administrator access is required to archive a tyre.');
        return;
      }
      setBusy(true, 'Archiving…');
      archiveConfirmBtn.textContent = 'Archiving…';
      try {
        // Re-read the live status immediately before archiving. The form's seed
        // may be stale if the tyre was fitted/changed in another session.
        const latest = await fetchTyre(archiveId);
        if (disposed) return;
        if (!latest.success || !latest.data) {
          setArchiveMsg(latest.status === 404
            ? 'This tyre is no longer in the register — it may already have been archived. Return to the register to refresh.'
            : 'Could not verify the tyre’s current status. Nothing was archived. Please try again.');
          return;
        }
        if (!isArchiveEligibleTyre(latest.data)) {
          archiveStillAllowed = false;
          setArchiveMsg('Not archived: this tyre is no longer Scrapped, Sold or Missing. Only tyres with one of those statuses can be archived. Return to the tyre detail to refresh.');
          return;
        }
        const res = await archiveTyre(archiveId);
        if (disposed) return;
        if (res.success) {
          if (typeof ctx.onArchived === 'function') ctx.onArchived(latest.data);
          return;
        }
        if (res.status === 409) {
          setArchiveMsg('Not archived: the tyre is still fitted to a sensor or part of an active retread batch. Remove it from the sensor and from any active retread batch first.');
        } else if (res.status === 403) {
          setArchiveMsg('Administrator access to this tyre’s customer is required.');
        } else if (res.status === 401) {
          setArchiveMsg('Your session has expired. Please sign in again.');
        } else if (res.status === 404) {
          setArchiveMsg('This tyre was not found or has already been archived. Return to the register to refresh the list.');
        } else {
          setArchiveMsg(res.message || 'Could not archive the tyre.');
        }
      } catch (_) {
        if (!disposed) setArchiveMsg('Could not archive the tyre. Check your connection and try again.');
      } finally {
        if (!disposed) {
          setBusy(false);
          archiveConfirmBtn.textContent = archiveConfirmLabel;
        }
      }
    });
  }

  // Confirm panel — now a single use: the duplicate tyre ID (upsert) guard.
  function showConflict(html, confirmLabel, onConfirm, onCancel = null) {
    conflictMsg.innerHTML = html;
    conflictConfirm.textContent = confirmLabel;
    conflictConfirm.onclick = onConfirm;
    conflictCancelAction = onCancel;
    conflictEl.hidden = false;
    submitBtn.disabled = true;
  }
  function hideConflict() {
    if (conflictEl) conflictEl.hidden = true;
    conflictCancelAction = null;
  }

  function buildPayload(values) {
    const p = {};
    bodyFields.forEach((f) => {
      // Never post the opening baseline once automatic pressure-distance tracking
      // has started. Omitting the property is important: sending the old value
      // back on an unrelated edit could overwrite a backend-maintained total.
      if (f.key === 'tyreKm' && !distanceEditable) return;

      const raw = values[f.key];
      if (isBlank(raw)) return;
      if (!numberKeys.has(f.key)) {
        p[f.key] = raw;
        return;
      }

      const n = Number(raw);
      p[f.key] = treadDepthKeys.has(f.key)
        ? Math.round((n + Number.EPSILON) * 100) / 100
        : n;
    });
    const cid = currentCustomerId() || seed.customerId;
    if (cid) p.customerId = cid;
    return p;
  }

  // Leave the form and hand back to the caller.
  function finish(created, message, kind = 'ok', delay = 600) {
    if (message) setMsg(message, kind);
    setTimeout(() => {
      if (disposed) return;
      if (!isEdit && typeof ctx.onCreated === 'function') ctx.onCreated(created);
      else if (typeof ctx.onClose === 'function') ctx.onClose();
    }, message ? delay : 0);
  }

  // POST /api/tyre. Returns the saved record, or null when it failed (having
  // already reported why).
  async function saveRecord(payload) {
    try {
      const res = await saveTyre(payload, { reason: isEdit ? 'edit' : 'create' });
      if (disposed) return null;
      if (!res.success) {
        const base = res.message || 'Could not save the tyre.';
        // A create the pre-check could not explain (e.g. a standard user, who
        // cannot see archived tyres) may be an archived ID. A 409 is where a
        // "that ID is taken" refusal belongs, so hint there — alongside the
        // server's message, not instead of it.
        setMsg(!isEdit && res.status === 409 ? `${base} ${ARCHIVED_ID_HINT}` : base, 'error');
        setBusy(false);
        return null;
      }
      return (res.data && res.data.externalTyreId) ? res.data : payload;
    } catch (e) {
      if (!disposed) { setMsg('Something went wrong while saving. Please try again.', 'error'); setBusy(false); }
      return null;
    }
  }

  async function fetchFreshTyre(externalTyreId, fallback) {
    try {
      const res = await fetchTyre(externalTyreId);
      if (!disposed && res?.success && res.data) return res.data;
    } catch (e) {
      // The association write already succeeded; a refresh failure should not
      // turn that success into an error. The detail view will refetch again.
    }
    return fallback;
  }

  async function completeWheelAssociation(created, sensorId) {
    const externalTyreId = created?.externalTyreId;
    if (!externalTyreId) return { success: false, message: 'The tyre was saved, but its ID was not returned.' };

    setMsg(`Tyre saved. Fitting to sensor ${sensorId}…`, 'ok');
    setBusy(true, 'Fitting…');

    let res;
    try {
      res = await associateTyreWithSensor(
        externalTyreId,
        { sensorId, ifExistingTyre: 'error' },
        { reason: 'fit' }
      );
    } catch (e) {
      return { success: false, message: 'The tyre was saved, but it could not be fitted to the wheel sensor.' };
    }

    if (disposed) return { success: false };

    if (res.success) {
      const fresh = await fetchFreshTyre(externalTyreId, created);
      if (typeof ctx.onAssociated === 'function') ctx.onAssociated(fresh);
      return { success: true, tyre: fresh };
    }

    if (res.status !== 409) {
      return {
        success: false,
        message: res.message || 'The tyre was saved, but it could not be fitted to the wheel sensor.'
      };
    }

    const otherTyreId = existingTyreIdFromAssociation(res);

    if (otherTyreId && String(otherTyreId).trim() === String(externalTyreId).trim()) {
      const fresh = await fetchFreshTyre(externalTyreId, created);
      if (typeof ctx.onAssociated === 'function') ctx.onAssociated(fresh);
      return { success: true, tyre: fresh };
    }

    if (!otherTyreId) {
      return {
        success: false,
        message:
          'The tyre was saved, but this sensor already has another tyre and the backend did not return its tyre ID, so it cannot be replaced safely.'
      };
    }

    setBusy(false);
    showConflict(
      `Sensor <b>${escapeHtml(sensorId)}</b> already has tyre <b>${escapeHtml(otherTyreId)}</b> fitted. ` +
      `Remove <b>${escapeHtml(otherTyreId)}</b> and fit tyre <b>${escapeHtml(externalTyreId)}</b> here instead?`,
      'Remove & fit',
      async () => {
        hideConflict();
        setBusy(true, 'Reassigning…');

        try {
          const cleared = await clearTyreSensorAssociation(otherTyreId, { reason: 'remove' });
          if (disposed) return;

          if (!cleared.success) {
            setMsg(
              cleared.message || `Tyre ${externalTyreId} was saved, but tyre ${otherTyreId} could not be removed from this sensor.`,
              'warn'
            );
            setBusy(false);
            return;
          }

          const retry = await associateTyreWithSensor(
            externalTyreId,
            { sensorId, ifExistingTyre: 'error' },
            { reason: 'fit' }
          );
          if (disposed) return;

          if (!retry.success) {
            setMsg(
              retry.message || `Tyre ${externalTyreId} was saved, but it still could not be fitted to sensor ${sensorId}.`,
              'warn'
            );
            setBusy(false);
            return;
          }

          const fresh = await fetchFreshTyre(externalTyreId, created);
          if (typeof ctx.onAssociated === 'function') ctx.onAssociated(fresh);
          finish(fresh, 'Tyre saved and fitted.');
        } catch (e) {
          if (!disposed) {
            setMsg(
              `Tyre ${externalTyreId} was saved, but something went wrong while reassigning the sensor.`,
              'warn'
            );
            setBusy(false);
          }
        }
      },
      () => {
        hideConflict();
        setBusy(false);
        finish(
          created,
          `Tyre ${externalTyreId} was saved to stock, but it was not fitted because the existing sensor association was kept.`,
          'warn',
          1500
        );
      }
    );

    return { success: false, pendingConflict: true, tyre: created };
  }

  // Save the tyre. When opened from a wheel, immediately fit the new record to
  // that wheel's sensor; normal dashboard create remains an In-Stock-only save.
  async function saveAndFinish(payload) {
    setBusy(true, 'Saving…');
    const created = await saveRecord(payload);
    if (!created || disposed) return;

    if (!isEdit && targetSensorId) {
      const association = await completeWheelAssociation(created, targetSensorId);
      if (disposed || association?.pendingConflict) return;

      if (association?.success) {
        finish(association.tyre || created, 'Tyre saved and fitted.');
        return;
      }

      setBusy(false);
      finish(
        created,
        association?.message || 'The tyre was saved to stock, but it could not be fitted to the selected wheel.',
        'warn',
        1500
      );
      return;
    }

    finish(created, isEdit ? 'Changes saved.' : 'Tyre saved.');
  }

  // The archived tyre with exactly this ID, or null. Administrators only — the
  // archived list is admin-only (403 otherwise) — and best-effort: any failure
  // returns null so the create proceeds and the server remains the authority.
  async function findArchivedTyre(id) {
    if (!canArchiveTyres()) return null;
    try {
      const res = await fetchArchivedTyres({ externalTyreId: id, limit: 1 });
      if (!res.success) return null;
      const rows = Array.isArray(res.data?.results) ? res.data.results : [];
      return rows.find((t) => String(t?.externalTyreId ?? '') === id) || null;
    } catch (_) {
      return null;
    }
  }

  function offerRestore(archived) {
    const id = String(archived.externalTyreId);
    showConflict(
      archivedIdConflictHTML(archived, { wheelContext: !!targetSensorId }),
      'Restore archived tyre',
      async () => {
        if (busy) return;
        conflictConfirm.disabled = true;
        setBusy(true, 'Restoring…');
        let res;
        try {
          res = await restoreTyre(id);
        } catch (_) {
          res = { success: false, status: 0 };
        }
        if (disposed) return;
        conflictConfirm.disabled = false;

        if (res.success) {
          hideConflict();
          const restored = (res.data && res.data.externalTyreId) ? res.data : { ...archived, deleted: false };
          setMsg(`Tyre ${id} restored with its full history. No sensor was fitted.`, 'ok');
          // Open the RESTORED tyre — not the new-tyre details typed here.
          setTimeout(() => {
            if (disposed) return;
            if (typeof ctx.onRestored === 'function') ctx.onRestored(restored);
            else if (typeof ctx.onClose === 'function') ctx.onClose();
          }, 600);
          return;
        }

        setBusy(false);
        submitBtn.disabled = true; // panel still open; Cancel re-enables Save
        if (res.status === 404) {
          // Restored by someone else meanwhile — the ID is live now, so the
          // normal duplicate-ID guard applies on the next Save.
          hideConflict();
          submitBtn.disabled = false;
          setMsg(`Tyre ${id} is no longer archived — it may just have been restored. Save again to see its current record.`, 'warn');
        } else if (res.status === 403) {
          setMsg('Administrator access to this tyre’s customer is required to restore it.', 'error');
        } else if (res.status === 401) {
          setMsg('Your session has expired. Please sign in again.', 'error');
        } else {
          setMsg(res.message || `Could not restore tyre ${id}. Please try again.`, 'error');
        }
      }
    );
    // The panel is about THIS id. Typing a different one dismisses it and
    // re-enables Save, so "enter a different ID" is one step, not two.
    if (idInput) {
      const onIdEdit = () => {
        if (idInput.value.trim() === id) return;
        idInput.removeEventListener('input', onIdEdit);
        if (busy) return;
        hideConflict();
        submitBtn.disabled = false;
        setMsg('', '');
      };
      idInput.addEventListener('input', onIdEdit);
      idInput.focus();
      idInput.select?.();
    }
  }

  async function submit() {
    if (busy) return;
    const values = readValues();
    const id = (values.externalTyreId || '').trim();
    if (!id) { setMsg('Please enter the tyre ID.', 'error'); if (idInput) idInput.focus(); return; }

    // A tyre must belong to a yard — enforced in both modes, so the legacy
    // yard-less records get one the moment they are edited.
    const yardId = (values.yardId || '').trim();

    if (!yardId) {
      if (yardSel?.disabled) {
        setMsg('Please create a yard before adding a tyre.', 'error');
      } else {
        setMsg('Please choose a yard for this tyre.', 'error');
        yardSel?.focus();
      }

      return;
    }

    const payload = buildPayload({ ...values, externalTyreId: id });

    // edit: the record already exists and we mean to update it — save directly
    // (still an upsert, so the chosen yardId is written).
    if (isEdit) { setMsg('', ''); saveAndFinish(payload); return; }

    // create: guard against overwriting an existing tyre (upsert guard).
    setMsg('', '');
    setBusy(true, 'Checking…');
    let existing = null;
    try { existing = await fetchTyre(id); } catch (e) { existing = null; }
    if (disposed) return;

    // Not a live tyre. It may still be an ARCHIVED one — whose ID the backend
    // will not let us reuse — so check before posting, and offer a restore.
    if (existing && !existing.success && existing.status === 404) {
      const archived = await findArchivedTyre(id);
      if (disposed) return;
      if (archived) {
        setBusy(false);
        offerRestore(archived);
        return;
      }
    }

    if (existing && existing.success && existing.data) {
      applyOpeningDistanceRule(existing.data);
      if (!distanceEditable) delete payload.tyreKm;

      setBusy(false);
      const distanceNote = !distanceEditable
        ? ' Its opening distance will not be changed because automatic distance tracking has already started.'
        : '';
      showConflict(
        `A tyre with ID <b>${escapeHtml(id)}</b> already exists. Saving will update its details ` +
        `rather than create a new tyre.${distanceNote} Continue?`,
        'Update existing tyre',
        () => { hideConflict(); saveAndFinish(payload); }
      );
      return;
    }

    saveAndFinish(payload);
  }

  submitBtn.addEventListener('click', submit);
  cancelBtn.addEventListener('click', () => { if (!busy && typeof ctx.onClose === 'function') ctx.onClose(); });
  if (conflictCancel) {
    conflictCancel.addEventListener('click', () => {
      const onCancel = conflictCancelAction;
      hideConflict();
      if (typeof onCancel === 'function') onCancel();
      else submitBtn.disabled = false;
    });
  }

  return () => { disposed = true; };
}
